import { listIntegrationConnections } from "@/cowork/api"
import {
  coworkIntegrationCatalog,
  integrationCategory,
  integrationConnectionProviderKey,
} from "@/cowork/integration-catalog"
import { useLanguage } from "@/context/language"
import { createMemo, createResource, For, Show } from "solid-js"

export function CoworkDataSourcesPage() {
  const language = useLanguage()
  const [connections] = createResource(() => listIntegrationConnections())
  const rows = createMemo(() =>
    (connections()?.connections ?? []).map((connection) => {
      const providerKey = integrationConnectionProviderKey(connection)
      const integration = coworkIntegrationCatalog.find((item) => item.providerKey === providerKey)
      return { connection, providerKey, integration }
    }),
  )

  return (
    <div class="min-h-full bg-v2-background-bg-deep px-4 py-7 sm:px-7 xl:px-10">
      <div class="mx-auto max-w-[1400px]">
        <header>
          <h1 class="text-[30px] font-semibold tracking-[-0.025em]">
            {language.t("cowork.enterprise.nav.dataSources")}
          </h1>
          <p class="mt-1 max-w-3xl text-[13px] text-v2-text-text-muted">
            {language.t("cowork.enterprise.dataSources.description")}
          </p>
        </header>
        <section class="mt-7 overflow-hidden rounded-xl border border-v2-border-border-base bg-v2-background-bg-base">
          <Show
            when={!connections.loading && rows().length > 0}
            fallback={
              <div class="grid min-h-56 place-items-center p-8 text-center text-[12px] text-v2-text-text-muted">
                {connections.loading ? language.t("common.loading") : language.t("cowork.enterprise.dataSources.empty")}
              </div>
            }
          >
            <div class="overflow-x-auto">
              <table class="w-full min-w-[680px] border-collapse text-left">
                <thead>
                  <tr class="border-b border-v2-border-border-base text-[10px] font-semibold uppercase tracking-[0.12em] text-v2-text-text-faint">
                    <th class="px-5 py-3">{language.t("cowork.enterprise.dataSources.name")}</th>
                    <th class="px-4 py-3">{language.t("cowork.enterprise.files.type")}</th>
                    <th class="px-4 py-3">{language.t("cowork.enterprise.dashboard.scope")}</th>
                    <th class="px-4 py-3">{language.t("cowork.usage.status")}</th>
                  </tr>
                </thead>
                <tbody>
                  <For each={rows()}>
                    {(row) => (
                      <tr class="border-b border-v2-border-border-muted last:border-0 hover:bg-v2-overlay-simple-overlay-hover">
                        <td class="px-5 py-4">
                          <div class="flex items-center gap-3">
                            <span class="grid size-9 shrink-0 place-items-center rounded-lg bg-v2-background-bg-layer-03 p-2">
                              <Show
                                when={row.integration?.logoPath}
                                fallback={<span class="text-[11px] font-semibold">DS</span>}
                              >
                                <img src={row.integration!.logoPath} alt="" class="max-h-full max-w-full" />
                              </Show>
                            </span>
                            <span class="text-[13px] font-medium">{row.integration?.label || row.providerKey}</span>
                          </div>
                        </td>
                        <td class="px-4 py-4 text-[12px] text-v2-text-text-muted">
                          {row.integration
                            ? language.t(integrationCategory(row.integration))
                            : language.t("cowork.enterprise.integrations.category.data")}
                        </td>
                        <td class="px-4 py-4">
                          <span class="rounded-md bg-v2-background-bg-layer-03 px-2 py-1 text-[10px] font-medium">
                            {language.t("cowork.enterprise.scope.allCompanies")}
                          </span>
                        </td>
                        <td class="px-4 py-4">
                          <span class="rounded-md bg-v2-state-bg-success px-2 py-1 text-[10px] font-medium text-v2-state-fg-success">
                            {language.t("cowork.integrations.connected")}
                          </span>
                        </td>
                      </tr>
                    )}
                  </For>
                </tbody>
              </table>
            </div>
          </Show>
        </section>
      </div>
    </div>
  )
}
